import React, { useEffect, useState } from 'react'
import { withRouter } from 'next/router'
import Layout from '../../components/Layout/Layout'
import styled from 'styled-components'
import { SCREEN_SIZE } from '../../lib/Media'
import Head from 'next/head'
import { get } from '../../lib/fetch'
import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider'
import RecipeSearchBanner from '../../components/Recipe/RecipeSearchBanner'
import RecipeList from '../../components/Recipe/RecipeList'
import { EventTrackingConsumer } from '../../components/EventTrackingProvider/EventTrackingProvider'
import {
  createRecipeSearchEvent,
  RECIPE_SEARCH_EVENT_ACTION as RSEA,
} from '../../components/Recipe/recipeUtils'
import { RecipeErrorBody } from '../../components/Recipe/RecipeErrorPage'
import dynamic from 'next/dynamic'
import { GTM_ENV_VAR as GEV, PAGE_TYPES } from '../../lib/eventTrackerFunction'
import RecipeLayout, {
  below,
  recipeBreakpoints,
} from '../../components/Recipe/RecipeLayout'

const RecipeErrorPage = dynamic(() =>
  import('../../components/Recipe/RecipeErrorPage')
)
import RecipeHomeBackLink from '../../components/Recipe/RecipeHomeBackLink'
import { setCtaLocationMeta } from '../../lib/utils'

const RECIPE_SEARCH_LAYOUT_NAME = 'RecipeSearch'
const PAGE_SIZE = 20

const MainContainer = styled.div`
  ${below(recipeBreakpoints.max)} {
    padding: 0 1.5rem;
  }

  ${SCREEN_SIZE.Below.MobileL} {
    padding: 0 1rem;
  }
`

const getSearchResult = data => {
  const layouts = data?.page?.layouts || []
  const recipeSearch = layouts.find(
    layout => layout.name === RECIPE_SEARCH_LAYOUT_NAME
  )
  return recipeSearch?.value
}

const fetchRecipeSearchResults = async (query, page = 1) => {
  if (!query) {
    return {}
  }
  try {
    const basePath = `layout/recipe/search`
    const path = `${basePath}?size=${PAGE_SIZE}&q=${query}&page=${page}`
    const response = await get(path)
    if (response?.data) {
      return {
        recipeSearch: response.data,
        statusCode: response.code,
      }
    }
    return { statusCode: response.code }
  } catch (e) {
    return { statusCode: 500 }
  }
}

const RecipeSearchWrp = props => {
  const { router, recipeSearch: recipeSearchData, statusCode, track } = props
  const [searchTerm, setSearchTerm] = useState(router.query.q)
  const recipeSearch = getSearchResult(recipeSearchData)
  const [searchResult, setSearchResult] = useState(recipeSearch)

  useEffect(() => {
    setSearchTerm(router.query.q)
  }, [router.query.q])

  useEffect(() => {
    setSearchResult(recipeSearch)
  }, [recipeSearch])

  useEffect(() => {
    if (router.query?.q && !searchResult) {
      return
    }
    if (!searchResult || searchResult.total === 0) {
      const event = createRecipeSearchEvent({
        [GEV.EVENT_ACTION]: RSEA.SEARCH_NO_RESULTS,
        [GEV.PAGE_TYPE]: 'Recipe Zero Search Result',
        [GEV.SEARCH_TERM]: router.query?.q || '',
        [GEV.SEARCH_RES_COUNT]: 0,
      })
      track(event)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResult])

  useEffect(() => {
    setCtaLocationMeta([router.query?.q || ''])
  })

  useEffect(() => {
    const searchKeywords = router.query?.q
    track({
      [GEV.PAGE_TYPE]: PAGE_TYPES.RECIPE_SEARCH,
      [GEV.SCREEN_NAME]: `lifestyle | search${
        searchKeywords ? ` | ${searchKeywords}` : ''
      }`,
      [GEV.EVENT_CAT]: 'recipe',
      [GEV.EVENT_ACTION]: 'landed',
      [GEV.EVENT_LABEL]: `${PAGE_TYPES.RECIPE_SEARCH}${
        searchKeywords ? ` - ${searchKeywords}` : ''
      }`,
      [GEV.ASSET_TYPE]: 'Lifestyle',
    })
  })

  if (router.query?.q && !searchResult) {
    return (
      <RecipeErrorPage
        categoryList={props.categoryList}
        organizationData={props.organizationData}
        errorCode={statusCode}
        pathname={router?.pathname}
      />
    )
  }

  return (
    <>
      <Head>
        <title>Recipes{router.query.q ? ` - ${router.query.q}` : ''}</title>
        <link rel="dns-prefetch" href="https://www.innit.com/" />
        <link rel="preconnect" href="https://www.innit.com/" />
        <meta property="og:title" content={`Recipes | ${router.query.q}`} />
        <meta
          name="description"
          key="meta_description"
          content="Create the perfect home-cooked meal with our wide library of easy cooking tips, meal ideas and food inspiration from FairPrice"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="FairPrice" />
      </Head>
      <NdsThemeProvider>
        <Layout
          categoryList={props.categoryList}
          organizationData={props.organizationData}
          noWrapper="noGap"
        >
          <RecipeLayout>
            <RecipeSearchBanner
              searchTerm={searchTerm}
              onSearchTermChange={setSearchTerm}
            />
            {!searchResult || searchResult.total === 0 ? (
              <RecipeErrorBody
                title="Nothing came up"
                description="Try searching with a different keyword or go back to Home."
                buttonText="Back to home"
                buttonLink="/recipes"
              />
            ) : (
              <MainContainer>
                <RecipeHomeBackLink />
                <RecipeList
                  items={searchResult.recipes}
                  pageType="Recipe Listing"
                  label={`Recipe Search - ${router.query.q}`}
                  headerTitle={`Results for '${router.query.q}'`}
                  headerDescription={`${searchResult.total} recipes found`}
                  hasMore={searchResult.page < searchResult.total / PAGE_SIZE}
                  ctaLocationMeta={[router.query?.q || '']}
                  loadMore={async () => {
                    if (!searchResult) {
                      return
                    }
                    const {
                      recipeSearch: newRecipeSearch,
                    } = await fetchRecipeSearchResults(
                      searchTerm,
                      searchResult.page + 1
                    )
                    if (newRecipeSearch) {
                      const newSearchResult = getSearchResult(newRecipeSearch)
                      setSearchResult({
                        ...newSearchResult,
                        recipes: [
                          ...searchResult.recipes,
                          ...newSearchResult.recipes,
                        ],
                      })
                    }
                  }}
                />
              </MainContainer>
            )}
          </RecipeLayout>
        </Layout>
      </NdsThemeProvider>
    </>
  )
}

const RecipeSearch = props => (
  <EventTrackingConsumer>
    {({ track }) => <RecipeSearchWrp {...props} track={track} />}
  </EventTrackingConsumer>
)

RecipeSearch.getInitialProps = async ctx => {
  return fetchRecipeSearchResults(ctx.query.q)
}

export default withRouter(RecipeSearch)
